import { TAccount } from '@helpers/Accounts/accounts.types';
import { getAccountIsLoanEnded } from '@helpers/Accounts/getAccountIsLoanEnded';
import { TApplication } from '@helpers/GetTopUpStatus/getTopUpStatus.types';
import {
    GetAccountsResponse,
    GetApplicationResponse
} from '@iwoca/lapi-client/edge';

import { MY_OFFERS_URL } from '../myOffers/MyOffers.sagas';

type Accounts = NonNullable<GetAccountsResponse['data']>['accounts'];

const PRODUCT_TYPES_WITH_URLS = ['cbils', 'recovery_loan'];
const DYNAMIC_DASHBORD_PRODUCT_TYPES = [
    'credit_facility',
    'flexi24',
    'merchant_cash_advance',
    'business_loan',
    'term_loan'
];

const productTypeToPathnameMapping = {
    credit_facility: '/',
    flexi24: '/',
    business_loan: '/',
    term_loan: '/',
    iwocapay: '/iwocapay/',
    cbils: '/CBILS/',
    recovery_loan: '/recovery-loan-scheme/',
    merchant_cash_advance: '/'
};

export const getDefaultDashboardLocation = (
    applicationResponse: GetApplicationResponse,
    accountsResponse: GetAccountsResponse
): Partial<Location> => {
    //redirect iwocapay only accounts
    if (
        hasExclusivelyIwocaPayAccount(accountsResponse, applicationResponse) &&
        !hasActiveNonIwocaPayApplication(applicationResponse)
    ) {
        return {
            pathname: productTypeToPathnameMapping['iwocapay']
        };
    }
    if (
        accountsResponse.data.accounts &&
        accountsResponse.data.accounts.length > 0
    ) {
        if (
            hasFundedAccount(accountsResponse) &&
            !hasNewApplicationState(accountsResponse, applicationResponse)
        ) {
            return getLocationForCurrentAccountProductType(accountsResponse);
        }
    }
    return { pathname: MY_OFFERS_URL };
};

const hasFundedAccount = (accountsResponse: GetAccountsResponse): boolean =>
    accountsResponse.data.accounts.some(
        (account) => !!account.account_id && account.product_type !== 'iwocapay'
    );

const getLocationForCurrentAccountProductType = (
    accountsResponse: GetAccountsResponse
): Partial<Location> => {
    const accounts = accountsResponse.data!.accounts;

    const hasDynamicDashboardAccount = accounts.some(
        (account) =>
            DYNAMIC_DASHBORD_PRODUCT_TYPES.includes(account.product_type) &&
            account.account_id &&
            !getAccountIsLoanEnded(account)
    );
    if (hasDynamicDashboardAccount) {
        return {
            pathname: '/'
        };
    }
    //redirect to ongoing accounts first, else default to first account in the list
    const firstOngoingNonIwocapayAccount = accounts.find(
        (account) =>
            account.account_id &&
            account.loan_status !== 'ended' &&
            PRODUCT_TYPES_WITH_URLS.includes(account.product_type)
    );

    if (firstOngoingNonIwocapayAccount) {
        return {
            pathname:
                productTypeToPathnameMapping[
                    getAccountProductType(firstOngoingNonIwocapayAccount)!
                ]
        };
    }

    return {
        pathname: '/'
    };
};

const getAccountProductType = (
    account: Accounts[0]
): Accounts[0]['product_type'] => {
    return account.product_type;
};

const hasNewApplicationState = (
    accountsResponse: GetAccountsResponse,
    applicationResponse: GetApplicationResponse
): boolean => {
    if (!applicationResponse.data || !accountsResponse) return false;

    const accounts = accountsResponse.data.accounts;
    const applicationStatus = applicationResponse.data.status;
    const requestedProducts = applicationResponse.data.requested_products ?? [];

    const hasOngoingFlexiorRBLAccount = accounts.some(
        (account) =>
            account.account_id &&
            !getAccountIsLoanEnded(account) &&
            DYNAMIC_DASHBORD_PRODUCT_TYPES.includes(account.product_type)
    );
    const hasUnfundedAccount = accounts.some((account) => !account.account_id);

    const hasNewApplicationDecision =
        ['declined', 'open', 'deferred'].includes(applicationStatus!) &&
        requestedProducts.length > 0 &&
        requestedProducts[0].product_type !== 'iwocapay';

    const hasNewFormalOffers = accounts.some(
        (account) =>
            account.formal_offers.length > 0 &&
            account.formal_offers.some(
                (formalOffer) =>
                    formalOffer.is_valid &&
                    DYNAMIC_DASHBORD_PRODUCT_TYPES.includes(
                        formalOffer.product_type!
                    )
            )
    );

    if (
        (hasNewApplicationDecision ||
            hasUnfundedAccount ||
            hasNewFormalOffers) &&
        !hasOngoingFlexiorRBLAccount
    )
        return true;

    for (const account of accounts) {
        if (!getAccountIsLoanEnded(account) || !applicationStatus) {
            return false;
        }
        //if Customer has an application for the ended account product type
        const hasMatchingRequestedProduct = requestedProducts.some(
            (requestedProduct) =>
                convertFlexi24ToCreditFacility(
                    requestedProduct.product_type
                ) === convertFlexi24ToCreditFacility(account.product_type)
        );
        const hasValidFormalOfferForProductType = account.formal_offers.some(
            (formalOffer) =>
                convertFlexi24ToCreditFacility(formalOffer.product_type!) ===
                convertFlexi24ToCreditFacility(account.product_type)
        );

        if (hasMatchingRequestedProduct || hasValidFormalOfferForProductType) {
            return hasNewApplicationDecision;
        }
    }

    return false;
};

//Flexi24 product type should be treated as a credit facility product type
const convertFlexi24ToCreditFacility = (
    productType:
        | TAccount['product_type']
        // @ts-expect-error type include requested_products
        | NonNullable<TApplication['requested_products']>[0]['product_type']
) => {
    if (productType === 'flexi24') return 'credit_facility';
    else return productType;
};

const hasExclusivelyIwocaPayAccount = (
    accountsResponse: GetAccountsResponse,
    applicationResponse: GetApplicationResponse
): boolean => {
    if (!applicationResponse.data) return false;

    const accounts = accountsResponse.data!.accounts;

    if (accounts.length > 0) {
        const nonIwocaPayAccount = accounts.find(
            (account) =>
                account.product_type && account.product_type !== 'iwocapay'
        );
        if (nonIwocaPayAccount) {
            return false;
        } else return true;
    } else if (hasIwocaPayApplication(applicationResponse)) {
        return true;
    } else return false;
};

export const hasIwocaPayApplication = (
    applicationResponse: GetApplicationResponse
): boolean => {
    if (!applicationResponse.data) return false;
    // TODO: Requested products is always in the response, but some tests
    // are failing due to bad fixtures.
    if (!applicationResponse.data.requested_products) return false;

    for (const requestedProducts of applicationResponse.data
        .requested_products) {
        //ignore offered applications
        if (
            !requestedProducts.suggested_product!.product_type ||
            !requestedProducts.suggested_product!.duration ||
            applicationResponse.data.status === 'offered'
        )
            continue;

        if (requestedProducts.suggested_product!.product_type === 'iwocapay') {
            return true;
        }
    }
    return false;
};

const hasActiveNonIwocaPayApplication = (
    applicationResponse: GetApplicationResponse
): boolean => {
    if (!applicationResponse.data) return false;
    // TODO: Requested products is always in the response, but some tests
    // are failing due to bad fixtures.
    if (!applicationResponse.data.requested_products) return false;

    for (const requestedProducts of applicationResponse.data
        .requested_products) {
        //ignore offered, deferred and declined applications
        if (
            !requestedProducts.suggested_product!.product_type ||
            !requestedProducts.suggested_product!.duration ||
            applicationResponse.data.status !== 'open'
        )
            continue;

        if (requestedProducts.suggested_product!.product_type !== 'iwocapay') {
            return true;
        }
    }
    return false;
};

export const hasValidIwocaPayFormalOffer = (
    accountsResponse: GetAccountsResponse
): boolean => {
    for (const account of accountsResponse.data!.accounts) {
        if (!account.formal_offers || account.formal_offers.length === 0)
            return false;
        else {
            if (account.formal_offers![0].product_type === 'iwocapay') {
                return true;
            }
        }
    }
    return false;
};
